import React from "react";
import { Button } from "@/design-system/components/react";
import { FaUpload, FaExclamationTriangle } from "../../utils/icons";
import { PATIENT_INFO_FIELD_COUNT } from "../../utils/dicomUpload";
import type { PhiFinding, StudyValidation } from "./types";

/**
 * Review / PHI-acknowledgment state (LU-A10 + LU-A4): study summary readout, the
 * identifying-tags warning, the **strip identifiers** option (Andrea), and the
 * rights/de-id acknowledgment gate. Full-width; actions right-aligned.
 *
 * Controlled — `stripIdentifiers` + `acknowledged` are owned by the page (logic
 * lane), which also performs the actual client-side stripping before building the
 * multipart body (LU-A5). See the plan: this changes the original "bytes as-is"
 * posture to "optionally de-identified on the client".
 */
interface PhiReviewPanelProps {
  validation: StudyValidation;
  phiFindings: PhiFinding[];
  stripIdentifiers: boolean;
  onStripChange: (next: boolean) => void;
  acknowledged: boolean;
  onAcknowledgeChange: (next: boolean) => void;
  /** When the user belongs to a company, offer routing the upload into the
   *  company-wide shared cohort instead of their private one. */
  hasCompany?: boolean;
  shareWithCompany?: boolean;
  onShareChange?: (next: boolean) => void;
  onUpload: () => void;
  onCancel: () => void;
}

function fmtBytes(bytes?: number): string | undefined {
  if (bytes == null) return undefined;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const PhiReviewPanel: React.FC<PhiReviewPanelProps> = ({
  validation,
  phiFindings,
  stripIdentifiers,
  onStripChange,
  acknowledged,
  onAcknowledgeChange,
  hasCompany = false,
  shareWithCompany = false,
  onShareChange,
  onUpload,
  onCancel,
}) => {
  const hasPhi = phiFindings.length > 0;
  const reviewHeading = hasPhi
    ? `Patient information detected · removal ${stripIdentifiers ? "enabled" : "disabled"}`
    : `Patient-information removal ${stripIdentifiers ? "enabled" : "disabled"}`;
  const reviewCopy = hasPhi
    ? stripIdentifiers
      ? "Removal is on. The detected supported fields below will be cleared on your device before upload."
      : "Removal is off. The detected supported fields below will leave your device and be uploaded unchanged."
    : stripIdentifiers
      ? `No values were found in the ${PATIENT_INFO_FIELD_COUNT} supported patient fields; removal remains enabled.`
      : `No values were found in the ${PATIENT_INFO_FIELD_COUNT} supported patient fields. Removal is off; unsupported fields are not inspected or cleared.`;
  return (
    <div data-testid="upload-phi-ack">
      {/* Study summary readout — one title + a single compact meta line (the
          page's "Review before upload" header already names this step). */}
      <div className="mb-2.5 rounded border border-border bg-surface px-3.5 py-2.5">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <span className="rounded-[1px] border border-signal-stroke-soft bg-signal-tint px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-signal-text-tint">
            {validation.modality ?? "CT"}
          </span>
          <span className="text-[13px] font-medium">Study validated · ready to send</span>
          <span className="ml-auto font-mono text-[10.5px] text-fg-muted">
            {validation.instanceCount} instances
            {validation.seriesCount != null ? ` · ${validation.seriesCount} series` : ""}
            {fmtBytes(validation.totalBytes) ? ` · ${fmtBytes(validation.totalBytes)}` : ""}
            {validation.acquiredDate ? ` · ${validation.acquiredDate}` : ""}
          </span>
        </div>
        <div className="mt-1 truncate font-mono text-[10px] text-fg/30">
          {validation.studyInstanceUid}
        </div>
      </div>

      {/* Patient-information warning + scrub option. It remains visible when
          nothing is detected because the scrub is default-on and detection of
          supported fields is not proof that the complete DICOM is PHI-free. */}
      <div
        className={[
          "mb-2.5 overflow-hidden rounded border transition-colors",
          stripIdentifiers
            ? "border-signal-stroke-soft bg-signal-ambient"
            : "border-warn/30 bg-warn-tint",
        ].join(" ")}
      >
        <div className="flex gap-3 px-3.5 py-3">
          <span
            className={[
              "grid h-[26px] w-[26px] flex-none place-items-center rounded-[2px] border",
              stripIdentifiers
                ? "border-signal-stroke-soft text-signal-text-tint"
                : "border-warn/30 text-warn",
            ].join(" ")}
          >
            <FaExclamationTriangle className="h-3.5 w-3.5" />
          </span>
          {/* Constant copy so toggling strip changes only color + the per-row
                "Removed" badges, never the box's size. */}
          <div>
            <h4 className="text-[13px] font-semibold">{reviewHeading}</h4>
            <p className="mt-1 text-xs leading-relaxed text-fg-muted">{reviewCopy}</p>
          </div>
        </div>

        {hasPhi && (
          <div className="max-h-32 overflow-y-auto custom-scrollbar">
            {phiFindings.map((f) => (
              <div
                key={f.tag}
                className={[
                  "flex items-center justify-between border-t px-3.5 py-1.5 font-mono text-[11px]",
                  stripIdentifiers ? "border-signal-stroke-soft/30" : "border-warn/15",
                ].join(" ")}
              >
                <span className="text-fg-muted">{f.tag}</span>
                <span className="flex items-center gap-2">
                  <span className={stripIdentifiers ? "text-fg/25 line-through" : "text-fg"}>
                    {f.value}
                  </span>
                  {stripIdentifiers && (
                    <span className="text-[9px] uppercase tracking-widest text-signal-text-tint">
                      Removed
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => onStripChange(!stripIdentifiers)}
          data-testid="upload-strip-toggle"
          aria-pressed={stripIdentifiers}
          className={[
            "flex w-full items-start gap-3 border-t px-3.5 py-2.5 text-left",
            stripIdentifiers ? "border-signal-stroke-soft/30" : "border-warn/15",
          ].join(" ")}
        >
          <span
            className={[
              "relative mt-0.5 h-[17px] w-[30px] flex-none rounded-full border transition-colors",
              stripIdentifiers ? "border-signal bg-signal" : "border-border-strong bg-surface-2",
            ].join(" ")}
          >
            <span
              className={[
                "absolute top-px h-[13px] w-[13px] rounded-full transition-transform",
                stripIdentifiers ? "translate-x-[14px] bg-white" : "translate-x-px bg-fg-muted",
              ].join(" ")}
            />
          </span>
          <span>
            <span className="block text-[12.5px] font-medium text-fg">
              Remove patient information before upload
            </span>
            <span className="mt-0.5 block text-[11.5px] leading-snug text-fg-muted">
              {stripIdentifiers ? "Enabled. Clears" : "Disabled. When enabled, clears"}{" "}
              {PATIENT_INFO_FIELD_COUNT} supported identity, demographic, contact, and visit fields.
              Study/acquisition timestamps, Accession Number, and all UIDs are always retained.
              Burned-in pixel text and private tags are not removed, so this is not full DICOM
              de-identification.
            </span>
          </span>
        </button>
      </div>

      {/* Destination — private (default) vs the company-wide shared cohort */}
      {hasCompany && (
        <div className="mb-3" data-testid="upload-destination">
          <div className="mb-1.5 text-[11px] font-mono uppercase tracking-wide text-fg-muted">
            Destination
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onShareChange?.(false)}
              aria-pressed={!shareWithCompany}
              data-testid="dest-private"
              className={[
                "flex-1 rounded border px-3 py-2 text-xs text-left transition-colors",
                !shareWithCompany
                  ? "border-signal-stroke-soft bg-signal-ambient text-fg"
                  : "border-border hover:border-border-strong bg-surface text-fg-muted",
              ].join(" ")}
            >
              Private to me
            </button>
            <button
              type="button"
              onClick={() => onShareChange?.(true)}
              aria-pressed={shareWithCompany}
              data-testid="dest-shared"
              className={[
                "flex-1 rounded border px-3 py-2 text-xs text-left transition-colors",
                shareWithCompany
                  ? "border-signal-stroke-soft bg-signal-ambient text-fg"
                  : "border-border hover:border-border-strong bg-surface text-fg-muted",
              ].join(" ")}
            >
              Share with my company
            </button>
          </div>
        </div>
      )}

      {/* Acknowledgment gate */}
      <button
        type="button"
        onClick={() => onAcknowledgeChange(!acknowledged)}
        data-testid="upload-ack-checkbox"
        aria-pressed={acknowledged}
        className={[
          "flex w-full items-start gap-3 rounded border px-3.5 py-2.5 text-left transition-colors",
          acknowledged
            ? "border-signal-stroke-soft bg-signal-ambient"
            : "border-border hover:border-border-strong bg-surface",
        ].join(" ")}
      >
        <span
          className={[
            "mt-px grid h-[18px] w-[18px] flex-none place-items-center rounded-[2px] border transition-colors",
            acknowledged ? "border-signal bg-signal" : "border-border-strong",
          ].join(" ")}
        >
          {acknowledged && (
            <span className="h-2 w-1 rotate-45 border-b-[1.6px] border-r-[1.6px] border-fg" />
          )}
        </span>
        <span
          className={`text-[12.5px] leading-relaxed ${acknowledged ? "text-fg" : "text-fg-muted"}`}
        >
          I have the rights to upload this study and confirm it is de-identified or consented for
          use.
        </span>
      </button>

      {/* Actions — right-aligned */}
      <div className="mt-3.5 flex justify-end gap-2.5">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onUpload} disabled={!acknowledged} data-testid="upload-submit">
          <FaUpload className="h-3.5 w-3.5" />
          {stripIdentifiers ? "Remove patient info & upload" : "Upload study"}
        </Button>
      </div>
    </div>
  );
};
